import React from 'react';
import { useEditor } from '@craftjs/core';
import { makeStyles, useTheme } from '@material-ui/core/styles'; 
import { Box, Button, IconButton, Tooltip, TextField, CircularProgress, Switch, FormControlLabel, Typography, Divider } from '@material-ui/core';
import UndoIcon from '@material-ui/icons/Undo';
import RedoIcon from '@material-ui/icons/Redo';
import SaveIcon from '@material-ui/icons/Save';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '45px',
    zIndex: 100,
    position: 'relative',
    padding: theme.spacing(0, 1.25),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
  },
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: theme.spacing(0, 2),
  },
  historyTools: {
    display: 'flex',
    alignItems: 'center',
  },
  saveTools: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  spacedSwitch: {
    marginLeft: theme.spacing(2),
  },
  icon: {
    color: `${theme.palette.type === 'dark' ? '#eee' : 'rgba(0, 0, 0, 0.8)'} !important`,
  }
}));

export const Header = ({ 
  templateName, onNameChange, onSave, isSaving, isLoading,
  isDuplicable, onDuplicableChange,
  isGlobal, onGlobalChange,
  isSuperadmin 
}) => {
  const { canUndo, canRedo, actions } = useEditor((state, query) => ({
    canUndo: query.history.canUndo(),
    canRedo: query.history.canRedo(),
  }));
  const classes = useStyles();
  const theme = useTheme(); 

  return (
    <Box className={classes.root}>
      <div className={classes.wrapper}>
        <div className={classes.historyTools}>
          <Tooltip title="Deshacer" placement="bottom">
            <span>
              <IconButton disabled={!canUndo || isSaving || isLoading} onClick={() => actions.history.undo()}>
                <UndoIcon className={classes.icon}/>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Rehacer" placement="bottom">
            <span>
              <IconButton disabled={!canRedo || isSaving || isLoading} onClick={() => actions.history.redo()}>
                <RedoIcon className={classes.icon}/>
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem style={{ margin: '0 16px' }} />

          <Tooltip 
            title="Permite que otros usuarios de la clínica dupliquen esta plantilla para usarla como base."
            placement="bottom"
          >
            <FormControlLabel
              control={
                <Switch
                  checked={isDuplicable}
                  onChange={(e) => onDuplicableChange(e.target.checked)}
                  name="isDuplicable"
                  color="primary"
                  disabled={isSaving || isLoading}
                />
              }
              label={<Typography variant="caption">Duplicable</Typography>}
              style={{ marginRight: 0 }} 
            />
          </Tooltip>
          
          {isSuperadmin && (
            <Tooltip 
              title="Hace que esta plantilla esté disponible para TODAS las clínicas del sistema."
              placement="bottom"
            >
              <FormControlLabel
                className={classes.spacedSwitch} 
                control={
                  <Switch
                    checked={isGlobal}
                    onChange={(e) => onGlobalChange(e.target.checked)}
                    name="isGlobal"
                    color="secondary"
                    disabled={isSaving || isLoading}
                  />
                }
                label={<Typography variant="caption">Global</Typography>}
                style={{ marginRight: 0 }} 
              />
            </Tooltip>
          )}
        </div>

        <div className={classes.saveTools}>
          <TextField
            placeholder="Nombre de la Plantilla"
            variant="outlined"
            size="small"
            value={templateName}
            onChange={e => onNameChange(e.target.value)}
            disabled={isSaving || isLoading}
            InputProps={{
              style: { 
                backgroundColor: theme.palette.background.paper 
              } 
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={onSave}
            disabled={isSaving || isLoading}
          >
            {isSaving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </div>
    </Box>
  );
};